/**
 * 波形对比组件
 * 显示原音和用户录音的波形对比
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  WaveformData,
  extractWaveformData,
  extractWaveformFromDataUrl,
  drawWaveform,
} from '../services/waveformDrawer';

interface WaveformCompareProps {
  /** 原音 Blob 或 URL */
  originalAudio?: Blob | string;
  /** 用户录音 DataURL */
  userAudioDataUrl?: string;
  /** 原音播放进度 (0-1) */
  originalProgress?: number;
  /** 用户录音播放进度 (0-1) */
  userProgress?: number;
}

export function WaveformCompare({
  originalAudio,
  userAudioDataUrl,
  originalProgress,
  userProgress,
}: WaveformCompareProps) {
  const originalCanvasRef = useRef<HTMLCanvasElement>(null);
  const userCanvasRef = useRef<HTMLCanvasElement>(null);

  const [originalData, setOriginalData] = useState<WaveformData | null>(null);
  const [userData, setUserData] = useState<WaveformData | null>(null);
  const [loadingOriginal, setLoadingOriginal] = useState(false);

  // 提取原音波形
  useEffect(() => {
    if (!originalAudio) {
      setOriginalData(null);
      return;
    }

    let cancelled = false;
    setLoadingOriginal(true);

    (async () => {
      try {
        let data: WaveformData;
        if (typeof originalAudio === 'string') {
          data = await extractWaveformFromDataUrl(originalAudio);
        } else {
          data = await extractWaveformData(originalAudio);
        }
        if (!cancelled) {
          setOriginalData(data);
        }
      } catch (err) {
        console.error('Failed to extract original waveform:', err);
      } finally {
        if (!cancelled) {
          setLoadingOriginal(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [originalAudio]);

  // 提取用户录音波形
  useEffect(() => {
    if (!userAudioDataUrl) {
      setUserData(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await extractWaveformFromDataUrl(userAudioDataUrl);
        if (!cancelled) {
          setUserData(data);
        }
      } catch (err) {
        console.error('Failed to extract user waveform:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userAudioDataUrl]);

  // 绘制原音波形
  useEffect(() => {
    const canvas = originalCanvasRef.current;
    if (!canvas || !originalData) return;

    // 设置 canvas 尺寸
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;

    drawWaveform({
      canvas,
      data: originalData,
      color: '#000000',
      progress: originalProgress,
      progressColor: '#7ec8e3',
    });
  }, [originalData, originalProgress]);

  // 绘制用户录音波形
  useEffect(() => {
    const canvas = userCanvasRef.current;
    if (!canvas || !userData) return;

    // 设置 canvas 尺寸
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;

    drawWaveform({
      canvas,
      data: userData,
      color: '#4ecdc4',
      progress: userProgress,
      progressColor: '#7ec8e3',
    });
  }, [userData, userProgress]);

  return (
    <div className="waveform-section">
      {/* 原音波形 */}
      <div className="waveform-row">
        <span className="waveform-label">原音</span>
        {loadingOriginal ? (
          <div className="waveform-canvas waveform-loading-inline">加载中...</div>
        ) : (
          <canvas ref={originalCanvasRef} className="waveform-canvas" />
        )}
      </div>

      {/* 用户录音波形 */}
      {userAudioDataUrl && (
        <div className="waveform-row">
          <span className="waveform-label">你的</span>
          <canvas ref={userCanvasRef} className="waveform-canvas" />
        </div>
      )}
    </div>
  );
}
